'use strict';

import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from '~/shared/src/utilities/metaModel';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Reference from '~/shared/src/ci/Reference';
import ReferenceProperty, {
    ReferencePropertyAttributes
} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty, {
    EnumReferencePropertyAttributes
} from '~/shared/src/ci/EnumReferenceProperty';

type AllowedPropertyAttributesToGet = ReferencePropertyAttributes & Pick<ReferenceProperty,
    'schema'
    | 'referencePropertySet'
    | 'valueLabel'
    | 'attributesModified'
>;
type AllowedPropertyMethodsToGet = Pick<ReferenceProperty,
    'getAttributeOriginalValue'
    | 'isAttributeModified'
    | 'isDeleted'
    | 'isHistorized'
    | 'isModified'
    | 'isNew'
    | 'isValid'
    | 'reset'
    | 'resetAttribute'
>;
type AllowedPropertyAttributesToSet = Partial<Pick<ReferencePropertyAttributes,
    'statusId'
    | 'value'
>>;

const allowedPropertyAttributesToGet = keys<AllowedPropertyAttributesToGet>() as string[];
const allowedPropertyMethodsToGet = keys<AllowedPropertyMethodsToGet>() as string[];
const allowedPropertyAttributesToSet = keys<AllowedPropertyAttributesToSet>() as string[];

type AllowedEnumPropertyAttributesToGet = EnumReferencePropertyAttributes & Pick<EnumReferenceProperty,
    'schema'
    | 'referencePropertySet'
    | 'isReorderingProperties'
>;
type AllowedEnumPropertyMethodsToGet = Pick<EnumReferenceProperty,
    'isModified'
    | 'isNew'
    | 'isValid'
    | 'reset'
    | 'isKeyUnique'
    | 'isOrderNumUnique'
    | 'getUniqueKeys'
    | 'getUniqueValues'
    | 'getMaxOrderNum'
    | 'addProperty'
    | 'addPropertiesFromJSON'
    | 'addPropertyFromJSON'
>;
type AllowedEnumPropertyPropertyAttributesToGet = AllowedPropertyAttributesToGet;
type AllowedEnumPropertyPropertyMethodsToGet = AllowedPropertyMethodsToGet;
type AllowedEnumPropertyPropertyAttributesToSet = Partial<
    Pick<
        ReferencePropertyAttributes,
        'statusId'
        | 'key'
        | 'value'
    >
    & { orderNum : number }
>;

const allowedEnumPropertyAttributesToGet = keys<AllowedEnumPropertyAttributesToGet>() as string[];
const allowedEnumPropertyMethodsToGet = keys<AllowedEnumPropertyMethodsToGet>() as string[];
const allowedEnumPropertyPropertyAttributesToGet = keys<AllowedEnumPropertyPropertyAttributesToGet>() as string[];
const allowedEnumPropertyPropertyMethodsToGet = keys<AllowedEnumPropertyPropertyMethodsToGet>() as string[];
const allowedEnumPropertyPropertyAttributesToSet = keys<AllowedEnumPropertyPropertyAttributesToSet>() as string[];

type MapAttributes = typeof Map.prototype;
const mapAttributes = keys<MapAttributes>();


class ReferencePropertyResolver {
    protected _reference : Reference;

    constructor( reference : Reference ) {
        this._reference = reference;

        return new Proxy( this, this );
    }

    get( target : ReferencePropertyResolver, propertyTypeName : string | symbol ) {
        let retVal : any = undefined;

        if ( typeof propertyTypeName === 'string' ) {
            const referencePropertySet = metaModel[ this._reference.schema ].getReferencePropertySetForReferenceSetAndPropertyType( this._reference.referenceSetId, _.upperFirst( propertyTypeName ) );

            if ( referencePropertySet ) {
                // return a proxy to avoid validate get and set
                if ( referencePropertySet.isEnumerated ) {
                    retVal = new Proxy(
                        referencePropertySet,
                        {
                            get: this.getEnumProperty.bind( this )
                        }
                    );
                }
                else {
                    retVal = new Proxy(
                        referencePropertySet,
                        {
                            get: this.getPropertyAttribute.bind( this ),
                            set: this.setPropertyAttribute.bind( this )
                        }
                    );
                }
            }
        }

        return retVal;
    }


    // property
    getPropertyAttribute( referencePropertySet : ReferencePropertySet, attribute : symbol ) : ( () => string ) | undefined;
    getPropertyAttribute( referencePropertySet : ReferencePropertySet, attribute : string ) : AttributeValue | undefined;
    getPropertyAttribute( referencePropertySet : ReferencePropertySet, attribute : string | symbol ) : AttributeValue | ( () => string ) | undefined {
        const property = this._reference.properties.get( referencePropertySet.propertyTypeId );

        let retVal : AttributeValue | ( () => string ) | undefined;

        if ( property ) {
            if ( typeof attribute === 'symbol' ) {
                if ( attribute === Symbol.toPrimitive ) {
                    retVal = () => {
                        return property.value as string;
                    };
                }
            }
            else {
                const attributeToGet = _.lowerFirst( attribute );

                if ( allowedPropertyAttributesToGet.includes( attributeToGet ) ) {
                    retVal = property[ attributeToGet as keyof Omit<typeof ReferenceProperty, 'prototype' | 'uniqueIdCount'> ];
                }
                else if ( allowedPropertyMethodsToGet.includes( attributeToGet ) ) {
                    retVal = ( property[ attributeToGet as keyof Omit<typeof ReferenceProperty, 'prototype' | 'uniqueIdCount'> ] as Function ).bind( property );
                }
                else {
                    throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.get.invalid', { attribute: attribute } );
                }
            }
        }

        return retVal;
    }

    setPropertyAttribute( referencePropertySet : ReferencePropertySet, attribute : string, value : AttributeValue ) {
        const attributeToSet = _.lowerFirst( attribute );

        let retVal = false;

        if ( allowedPropertyAttributesToSet.includes( attributeToSet ) ) {
            const propertyExists = this._reference.properties.has( referencePropertySet.propertyTypeId ),
                  property       = propertyExists
                      ? this._reference.properties.get( referencePropertySet.propertyTypeId )!
                      : this._reference.addProperty( referencePropertySet.propertyTypeId );

            try {
                if ( attributeToSet === 'statusId' && typeof value === 'number' ) {
                    property.statusId = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'value' && referencePropertySet.isValueSettable ) {
                    property.value = value;
                    retVal = true;
                }
            }
            catch ( e ) {
                // delete just created property if set is invalid
                if ( !propertyExists ) {
                    this._reference.properties.delete( referencePropertySet.propertyTypeId );
                }

                throw e;
            }
        }
        else {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.set.invalid', { attribute: attribute } );
        }

        return retVal;
    }


    // enumerated property
    getEnumProperty( referencePropertySet : ReferencePropertySet, attribute : string ) {
        const attributeToGet = _.lowerFirst( attribute );

        let retVal : any = undefined;

        // proxy for getting the properties of the enumerated property
        if ( attributeToGet === 'properties' ) {
            retVal = new Proxy(
                referencePropertySet,
                {
                    get: this.getEnumPropertyProperty.bind( this )
                }
            )
        }

        // get the attribute or method if exists
        else {
            let enumProperty = this._reference.enumProperties.get( referencePropertySet.propertyTypeId );

            const enumPropertyExists = !!enumProperty;

            if (
                !enumPropertyExists
                && (
                    attributeToGet === 'addProperty'
                    || attributeToGet === 'addPropertiesFromJSON'
                    || attributeToGet === 'addPropertyFromJSON'
                )
            ) {
                enumProperty = this._reference.addEnumProperty( referencePropertySet.propertyTypeId );
            }

            if ( enumProperty ) {
                try {
                    if ( allowedEnumPropertyAttributesToGet.includes( attributeToGet ) ) {
                        retVal = enumProperty[ attributeToGet as keyof Omit<typeof EnumReferenceProperty, 'prototype'> ];
                    }
                    else if ( allowedEnumPropertyMethodsToGet.includes( attributeToGet ) ) {
                        retVal = ( enumProperty[ attributeToGet as keyof Omit<typeof EnumReferenceProperty, 'prototype'> ] as Function ).bind( enumProperty );
                    }
                    else {
                        throw new LocalizableError( 'YN.base.dataObject.enumProperty.attributes.validation.get.invalid', { attribute: attribute } );
                    }
                }
                catch ( e ) {
                    // delete just created property if set is invalid
                    if ( !enumPropertyExists ) {
                        this._reference.enumProperties.delete( referencePropertySet.propertyTypeId );
                    }

                    throw e;
                }
            }
        }

        return retVal;
    }

    // enumerated property's property
    getEnumPropertyProperty( referencePropertySet : ReferencePropertySet, keyOrIndex : symbol ) : undefined;
    getEnumPropertyProperty( referencePropertySet : ReferencePropertySet, keyOrIndex : string ) : any;
    getEnumPropertyProperty( referencePropertySet : ReferencePropertySet, keyOrIndex : string | symbol ) {
        const enumProperty = this._reference.enumProperties.get( referencePropertySet.propertyTypeId );

        let retVal : any = undefined;

        if ( mapAttributes.includes( keyOrIndex as keyof MapAttributes ) ) {
            if ( enumProperty ) {
                retVal = enumProperty.properties[ keyOrIndex as keyof MapAttributes ];

                if ( typeof retVal === 'function' ) {
                    retVal = retVal.bind( enumProperty.properties );
                }
            }
        }
        else if ( typeof keyOrIndex !== 'symbol' ) {
            let convertedKeyOrIndex : number | string = keyOrIndex;

            if ( String( Number( keyOrIndex ) ) === keyOrIndex ) {
                convertedKeyOrIndex = Number( keyOrIndex );
            }

            retVal = new Proxy(
                {
                    referencePropertySet,
                    keyOrIndex: convertedKeyOrIndex
                },
                {
                    get: this.getEnumPropertyPropertyAttribute.bind( this ),
                    set: this.setEnumPropertyPropertyAttribute.bind( this )
                }
            );
        }

        return retVal;
    }

    getEnumPropertyPropertyAttribute( target : {
        referencePropertySet : ReferencePropertySet;
        keyOrIndex : number | string;
    }, attribute : symbol ) : ( () => string ) | undefined;
    getEnumPropertyPropertyAttribute( target : {
        referencePropertySet : ReferencePropertySet;
        keyOrIndex : number | string;
    }, attribute : string ) : AttributeValue | undefined;
    getEnumPropertyPropertyAttribute( target : {
        referencePropertySet : ReferencePropertySet;
        keyOrIndex : number | string;
    }, attribute : string | symbol ) : AttributeValue | ( () => string ) | undefined {
        const referencePropertySet = target.referencePropertySet,
              keyOrIndex           = target.keyOrIndex,
              enumProperty         = this._reference.enumProperties.get( referencePropertySet.propertyTypeId );

        let retVal : AttributeValue | ( () => string ) | undefined;

        if ( enumProperty ) {
            const property = this.getPropertyFromEnumProperty( enumProperty, keyOrIndex );

            if ( property ) {
                if ( typeof attribute === 'symbol' ) {
                    if ( attribute === Symbol.toPrimitive ) {
                        retVal = () => {
                            return property.value as string;
                        };
                    }
                }
                else {
                    const attributeToGet = _.lowerFirst( attribute );

                    if ( allowedEnumPropertyPropertyAttributesToGet.includes( attributeToGet ) ) {
                        retVal = property[ attributeToGet as keyof Omit<typeof ReferenceProperty, 'prototype' | 'uniqueIdCount'> ];
                    }
                    else if ( allowedEnumPropertyPropertyMethodsToGet.includes( attributeToGet ) ) {
                        retVal = ( property[ attributeToGet as keyof Omit<typeof ReferenceProperty, 'prototype' | 'uniqueIdCount'> ] as Function ).bind( property );
                    }
                    else {
                        throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.get.invalid', { attribute: attribute } );
                    }
                }
            }
        }

        return retVal;
    }

    setEnumPropertyPropertyAttribute( target : {
        referencePropertySet : ReferencePropertySet;
        keyOrIndex : number | string;
    }, attribute : string, value : AttributeValue ) {
        const referencePropertySet = target.referencePropertySet,
              keyOrIndex           = target.keyOrIndex,
              attributeToSet       = _.lowerFirst( attribute );

        let retVal = false;

        if ( allowedEnumPropertyPropertyAttributesToSet.includes( attributeToSet ) ) {
            const enumPropertyExists = this._reference.enumProperties.has( referencePropertySet.propertyTypeId ),
                  enumProperty       = enumPropertyExists
                      ? this._reference.enumProperties.get( referencePropertySet.propertyTypeId )!
                      : this._reference.addEnumProperty( referencePropertySet.propertyTypeId ),
                  existingProperty   = this.getPropertyFromEnumProperty( enumProperty, keyOrIndex ),
                  propertyExists     = !!existingProperty,
                  property           = existingProperty
                      ? existingProperty
                      : enumProperty.addProperty( keyOrIndex );

            try {
                if ( attributeToSet === 'statusId' && typeof value === 'number' ) {
                    property.statusId = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'orderNum' && typeof value === 'number' && ( referencePropertySet.isKeyOrdered || referencePropertySet.isValueOrdered ) ) {
                    property.orderNum = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'key' && referencePropertySet.isKeySettable ) {
                    property.key = value;
                    retVal = true;
                }
                else if ( attributeToSet === 'value' && referencePropertySet.isValueSettable ) {
                    property.value = value;
                    retVal = true;
                }
            }
            catch ( e ) {
                // delete just created property if set is invalid
                if ( !enumPropertyExists ) {
                    this._reference.enumProperties.delete( referencePropertySet.propertyTypeId );
                }
                else if ( !propertyExists ) {
                    enumProperty.properties.delete( property.stringKey );
                }

                throw e;
            }
        }
        else {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.set.invalid', { attribute: attribute } );
        }

        return retVal;
    }

    getPropertyFromEnumProperty( enumProperty : EnumReferenceProperty, keyOrIndex : number | string ) : ReferenceProperty | undefined {
        let retVal : ReferenceProperty | undefined = undefined;

        // get property by index (key sorted asc)
        if ( typeof keyOrIndex === 'number' ) {
            const properties : ReferenceProperty[] = [ ...enumProperty.properties.values() ];

            properties.sort( ( property1, property2 ) => {
                if ( property1.key && property2.key ) {
                    if ( property1.key > property2.key ) {
                        return 1;
                    }
                    else if ( property1.key < property2.key ) {
                        return -1;
                    }
                }

                return 0;
            } );

            retVal = properties[ keyOrIndex ];
        }

        // get property by stringKey
        else if ( enumProperty.properties.has( keyOrIndex ) ) {
            retVal = enumProperty.properties.get( keyOrIndex );
        }

        // get property by key (only the first)
        else {
            const properties : ReferenceProperty[] = [ ...enumProperty.properties.values() ];

            for ( let i = 0; i < properties.length; i++ ) {
                if ( properties[ i ].key === keyOrIndex ) {
                    retVal = properties[ i ];
                    break;
                }
            }
        }

        return retVal;
    }
}

export default ReferencePropertyResolver;
